import {
  createDutyResponse,
  getDutyRequest,
  mapClientError,
  queryKeys,
  updateDutyRequest,
} from "@dutyon/apis";
import {
  Alert,
  Button,
  LoadingOverlay,
  modals,
  notifications,
  Stack,
  Text,
  Title,
  TitleContainer,
} from "@dutyon/ui-kit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import { Plus } from "lucide-react";
import { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DutyRequestInfo } from "../../components/duty/DutyRequestInfo";
import { DutyResponseEditor } from "../../components/duty/DutyResponseEditor";

export function DutyRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // 근무 요청 상세 조회
  const {
    data: requestData,
    isLoading: isRequestLoading,
    isError: isRequestError,
    error: requestError,
    isFetching: isRequestFetching,
    refetch: refetchRequest,
  } = useQuery({
    queryKey: queryKeys.dutyRequests.detail(id),
    queryFn: () => {
      if (!id) throw new Error("Invalid ID");
      return getDutyRequest(id);
    },
    enabled: !!id,
  });

  const teamId = requestData?.teamId;
  const linkedResponse = requestData?.dutyResponses?.find(
    (response) => hash === `#duty-response-${response.id}`,
  )?.id;

  useEffect(() => {
    if (!linkedResponse) return;
    const frame = requestAnimationFrame(() => {
      document
        .getElementById(`duty-response-${linkedResponse}`)
        ?.scrollIntoView({ block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [linkedResponse]);

  const { mutate: handleCreateDutyResponse, isPending: isCreating } = useMutation({
    mutationFn: async () => {
      if (!requestData || !teamId) return;

      return await createDutyResponse({
        dutyRequestId: requestData.id,
      });
    },
    onSuccess: () => {
      notifications.show({
        title: "성공",
        message: "새 근무표(Duty Response)가 생성되었습니다.",
        color: "green",
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.dutyRequests.detail(id) });
    },
    onError: (error: unknown) => {
      notifications.show({
        title: "실패",
        message: mapClientError(error, { fallback: "근무표를 생성하지 못했습니다." }).message,
        color: "red",
      });
    },
  });

  const { mutate: handleComplete, isPending: isCompleting } = useMutation({
    mutationFn: async () => {
      if (!requestData) return;
      await updateDutyRequest({
        id: requestData.id,
        data: { status: "APPROVED" },
      });
    },
    onSuccess: () => {
      notifications.show({
        title: "성공",
        message: "최종 확정 처리되었습니다.",
        color: "green",
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.dutyRequests.detail(id) });
      navigate("/duty-requests");
    },
    onError: (error: unknown) => {
      notifications.show({
        title: "실패",
        message: mapClientError(error, { fallback: "근무 요청을 확정하지 못했습니다." }).message,
        color: "red",
      });
    },
  });

  const handleConfirm = () => {
    modals.openConfirmModal({
      title: "최종 완료",
      centered: true,
      children: (
        <Text size="sm">
          요청을 최종 확정하시겠습니까? 확정 후에는 요청자가 확인할 수 있습니다.
        </Text>
      ),
      labels: { confirm: "확정", cancel: "취소" },
      onConfirm: () => handleComplete(),
    });
  };

  const isLoading = isRequestLoading;
  const dutyResponses = requestData?.dutyResponses || [];

  if (!requestData && isRequestError) {
    const error = mapClientError(requestError, { fallback: "근무 요청을 불러오지 못했습니다." });
    return (
      <TitleContainer title="근무 요청 상세">
        <Stack gap="sm">
          <Alert color="red" title={error.title}>
            {error.message}
          </Alert>
          {error.retryable && (
            <Button loading={isRequestFetching} onClick={() => void refetchRequest()}>
              다시 조회
            </Button>
          )}
        </Stack>
      </TitleContainer>
    );
  }

  if (!requestData && !isLoading) {
    return (
      <TitleContainer title="근무 요청 상세">
        <Text>근무 요청을 찾을 수 없습니다.</Text>
      </TitleContainer>
    );
  }

  return (
    <TitleContainer
      title={requestData ? `${requestData.nurse?.user?.name}님의 근무 요청` : "근무 요청 상세"}
      rightSection={
        requestData?.status === "PENDING" && (
          <Button onClick={handleConfirm} loading={isCompleting}>
            요청 확정 처리
          </Button>
        )
      }
      style={{ position: "relative", minHeight: "200px" }}
    >
      <LoadingOverlay visible={isLoading || isCreating || isCompleting} zIndex={1000} />

      {requestData && (
        <Stack gap="lg">
          <DutyRequestInfo request={requestData} />

          <Title order={3} size="h4">
            생성된 Duty Response 목록
          </Title>

          {isEmpty(dutyResponses) ? (
            <Text c="dimmed" size="sm">
              생성된 근무표가 없습니다. 새 근무표를 생성하세요.
            </Text>
          ) : (
            dutyResponses.map((response) => (
              <div key={response.id} id={`duty-response-${response.id}`}>
                <DutyResponseEditor dutyResponse={response} dutyRequest={requestData} />
              </div>
            ))
          )}

          {requestData.status === "PENDING" && (
            <Button
              variant="default"
              fullWidth
              onClick={() => handleCreateDutyResponse()}
              loading={isCreating}
              leftSection={<Plus size={16} />}
              mt="md"
            >
              새 근무표 추가
            </Button>
          )}
        </Stack>
      )}
    </TitleContainer>
  );
}
